import './style/salesProductDetails.scss';

import {AwesomeIcon, CloseButton} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';

import {PeriodLabel, SalesProductView} from '../../../util/enums';

import {ActivateHandle} from '../../../util/interfaces';

import {GeneralSalesProduct} from '../../../util/types';

import SalesProductStatsController from './salesProductStats/salesProductStatsController';

import IDate from '@dvrd/idate';

import EditSalesProductController from './edit/editSalesProductController';
import {Icon} from '@iconify/react';
import {useTranslation} from 'react-i18next';

interface Props {
    onReload: VoidFunction;
    countryCodes: string;
}

function SalesProductDetailsController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onReload, countryCodes} = props;
    const [view, setView] = useState<SalesProductView>(SalesProductView.STATS);
    const [active, setActive] = useState(false);
    /** We use contentMounted to delay rendering the graphs, which allows React to render the opening of the slide-in
     * first. Without it, the slide-in animation is delayed 200-300ms because of the expensive rendering of the graphs.
     * */
    const [contentMounted, setContentMounted] = useState(false);
    const product = useRef<GeneralSalesProduct | null>(null);
    const accountIDs = useRef<Set<string>>(new Set());
    const period = useRef<PeriodLabel>(PeriodLabel.LAST_30_DAYS);
    const customPeriodStart = useRef<IDate | null>(null);
    const customPeriodEnd = useRef<IDate | null>(null);
    const {t} = useTranslation();

    function onActivate(data: {
        product: GeneralSalesProduct | null;
        accountIDs: Set<string>;
        period: PeriodLabel;
        customStart: IDate | null;
        customEnd: IDate | null;
        view?: SalesProductView;
    }) {
        product.current = data.product;
        accountIDs.current = data.accountIDs;
        period.current = data.period;
        customPeriodStart.current = data.customStart;
        customPeriodEnd.current = data.customEnd;
        setView(data.view ?? SalesProductView.STATS);
        setActive(true);
        requestAnimationFrame(() => requestAnimationFrame(() => setContentMounted(true)));
    }

    function onClickBackground(evt: React.MouseEvent) {
        if (evt.target === evt.currentTarget) onClose();
    }

    function onClose() {
        setActive(false);
        setView(SalesProductView.STATS);
        window.setTimeout(() => {
            setContentMounted(false);
        }, 250);
    }

    function onChangeView(view: SalesProductView) {
        return function () {
            setView(view);
        };
    }

    function onEditSuccess() {
        onReload();
    }

    function onCloseEdit() {
        setView(SalesProductView.STATS);
    }

    function renderHeader() {
        const _product = product.current;
        if (!_product) return renderGlobalHeader();
        const imageURL = _product.image_url;
        const title = _product.title;
        const ean = _product.ean;
        return (
            <div className='product-header'>
                {!imageURL ? (
                    <AwesomeIcon name='image' className='image-icon' />
                ) : (
                    <img src={imageURL} alt={title ?? ''} className='product-image' />
                )}
                <div className='product-identifiers'>
                    <label className='product-title'>{title ?? ''}</label>
                    <label className='ean'>EAN: {ean}</label>
                </div>
                <CloseButton onClick={onClose} className='btn-close' />
            </div>
        );
    }

    function renderGlobalHeader() {
        return (
            <div className='product-header global'>
                <div className='icon-container'>
                    <Icon icon='lucide:chart-no-axes-combined' className='chart-icon' />
                </div>
                <div className='product-identifiers'>
                    <label className='product-title'>
                        {t('salesDashboardV2.productDetails.salesProductStats.globalPerformance', 'Globale Prestaties')}
                    </label>
                    <label className='ean'>
                        {t(
                            'salesDashboardV2.productDetails.salesProductStats.allProductsCumulative',
                            'Alle producten opgeteld'
                        )}
                    </label>
                </div>
                <CloseButton onClick={onClose} className='btn-close' />
            </div>
        );
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <>
            <div
                className={classNames('sales-product-details-background', active && 'active')}
                onClick={onClickBackground}
            />
            <div className={classNames('sales-product-details', active && 'active')}>
                {renderHeader()}
                {active && contentMounted && view === SalesProductView.STATS && (
                    <SalesProductStatsController
                        onClose={onClose}
                        product={product.current}
                        accountIDs={accountIDs.current}
                        onChangeView={onChangeView}
                        period={period.current}
                        customPeriodStart={customPeriodStart.current}
                        customPeriodEnd={customPeriodEnd.current}
                        countryCodes={countryCodes}
                    />
                )}
                {view === SalesProductView.EDIT_PRODUCT && product.current && (
                    <EditSalesProductController
                        onEditSuccess={onEditSuccess}
                        onClose={onCloseEdit}
                        product={product.current}
                        accountIDs={accountIDs.current}
                    />
                )}
            </div>
        </>
    );
}

SalesProductDetailsController.displayName = 'SalesProductDetailsController';

export default forwardRef<ActivateHandle, Props>(SalesProductDetailsController);
